import { BulkOrderStatus } from '../configuration/config';
import {
  ITO,
  Order,
  GetOrders_AllCommandInput,
  GetOrdersResponseContent,
  SubmitOrderCommandInput,
  SubmitOrderCommandOutput,
  UpdateOrderStatusBatchCommandInput,
} from '@amzn/ito-client';
import { SentryFetchHttpHandler } from '@amzn/sentry-fetch-http-handler';
import { BulkOrderSource } from '../components/BulkOrdersTemplates/interfaces';
import { CSRFService } from './csrf-service';
import { API_BASE_URL } from '../configuration/config';
import { getString } from '../common/ui-string-labels/ui-strings-utils';

/**
 * ITO consumer type for bulk orders (itsam consumer)
 */
type BulkOrderConsumer = 'itsam';

/**
 * Mapping of consumers to their proxy endpoints
 */
const proxyMapping: { [key in BulkOrderConsumer]: string } = {
  itsam: '/ito-itsam',
};

const CSRF_TOKEN_HEADER = 'x-csrf-token';
const ERROR_STRING_PATH = 'bulkOrders.flashbar';

export interface SubmitBulkOrdersRequest {
  vendor: string;
  templates: BulkOrderSource[];
}

export interface BulkOrdersListResponse {
  orders: Order[];
  total: number;
  nextToken?: string;
}

/**
 * Error thrown when bulk order submission partially succeeds.
 * Contains the orders that were created before the failure.
 */
export class BulkOrderPartialError extends Error {
  constructor(
    message: string,
    public readonly createdOrders: Order[],
    public readonly failedTemplates: BulkOrderSource[],
  ) {
    super(message);
  }
}

/**
 * Service for Bulk Orders via the ITO itsam consumer.
 * Based on the ITShopITSEWebsite ITOOrdersService pattern.
 */
export class BulkOrdersService {
  private static _instance: BulkOrdersService;

  public static get instance(): BulkOrdersService {
    if (!BulkOrdersService._instance) {
      BulkOrdersService._instance = new BulkOrdersService();
    }
    return BulkOrdersService._instance;
  }

  private client: ITO;
  private requestHandler: SentryFetchHttpHandler;
  private csrfService: CSRFService;

  constructor(consumer?: BulkOrderConsumer) {
    this.requestHandler = new SentryFetchHttpHandler();

    const selectedConsumer = consumer ?? 'itsam';

    this.client = new ITO({
      endpoint: API_BASE_URL + proxyMapping[selectedConsumer],
      region: '*',
      credentials: { accessKeyId: '', secretAccessKey: '' },
      requestHandler: this.requestHandler as any,
    });

    this.csrfService = new CSRFService();
  }

  /**
   * Retrieves a security token for CSRF protection.
   */
  private async getSecurityToken(): Promise<string> {
    return this.csrfService.getToken();
  }

  /**
   * Submit bulk orders — creates one ITO order per template.
   */
  public async submitBulkOrders(request: SubmitBulkOrdersRequest): Promise<Order[]> {
    const csrfToken = await this.getSecurityToken();
    const createdOrders: Order[] = [];

    for (const template of request.templates) {
      this.requestHandler.pushHeader(CSRF_TOKEN_HEADER, csrfToken);

      const submitInput: SubmitOrderCommandInput = {
        items: {
          [template.productTaxonomy]: {
            quantity: template.quantity || 1,
            details: {
              costCenter: template.costCenter,
              bulkCost: template.cost,
            } as Record<string, any>,
          },
        },
        details: {
          vendor: request.vendor,
          orderType: 'bulk',
        },
      };

      try {
        const order = await this.client.submitOrder(submitInput);
        createdOrders.push(order as unknown as Order);
      } catch (error) {
        throw new BulkOrderPartialError(
          getString(`${ERROR_STRING_PATH}.error`),
          createdOrders,
          request.templates.slice(createdOrders.length),
        );
      }
    }

    return createdOrders;
  }

  /**
   * Retrieves all bulk orders based on the provided input parameters.
   * Uses ITO offset-based pagination (pageSize + nextToken as offset string).
   */
  public async listBulkOrders(params?: {
    vendor?: string;
    status?: string;
    pageSize?: number;
    pageIndex?: number;
    dateFilter?: [string, string];
    filters?: { [key: string]: string[] };
  }): Promise<BulkOrdersListResponse> {
    const pageSize = params?.pageSize || 10;
    const pageIndex = params?.pageIndex || 1;

    const input: GetOrders_AllCommandInput = {
      pageSize,
      nextToken: (pageIndex - 1).toString(),
      filters: {
        ...(params?.vendor && { 'details.vendor': [params.vendor] }),
        ...(params?.status && { status: [params.status] }),
        ...(params?.dateFilter && { 'requested.on': params.dateFilter }),
        ...params?.filters,
      },
    };

    try {
      const response: GetOrdersResponseContent = await this.client.getOrders_All(input);
      const orders = response.orders || [];

      return {
        orders,
        total: response.totalResults ?? orders.length,
        nextToken: response.nextToken ?? undefined,
      };
    } catch (error) {
      // Preserve the backend's descriptive message/code (modeled ITO exception)
      // instead of masking it with a generic string.
      throw error instanceof Error ? error : new Error(getString(`${ERROR_STRING_PATH}.error`));
    }
  }

  /**
   * Update order status (e.g., to COMPLETED, CANCELLED).
   * Gets a fresh CSRF token before the mutation.
   */
  public async updateOrderStatus(orderId: string, newStatus: BulkOrderStatus): Promise<void> {
    return this.updateOrdersStatus([orderId], newStatus);
  }

  /**
   * Update the status of multiple orders in a single batch call (e.g., to COMPLETED, CANCELLED).
   * Gets a fresh CSRF token before the mutation.
   */
  public async updateOrdersStatus(orderIds: string[], newStatus: BulkOrderStatus): Promise<void> {
    const csrfToken = await this.getSecurityToken();
    this.requestHandler.pushHeader(CSRF_TOKEN_HEADER, csrfToken);

    const input: UpdateOrderStatusBatchCommandInput = {
      orders: orderIds.map(orderId => ({ orderId, status: newStatus })),
    };

    try {
      await this.client.updateOrderStatusBatch(input);
    } catch (error) {
      // Preserve the backend's descriptive message/code (modeled ITO exception)
      // instead of masking it with a generic string.
      throw error instanceof Error ? error : new Error(getString(`${ERROR_STRING_PATH}.error`));
    }
  }
}
